import { useEffect, useState } from "react";
import { ArrowUpRight, UserCircle, ClipboardCheck, TrendingUp, Target, Dumbbell, Users } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { Loading } from "@/components/shared";

const ICONS = [UserCircle, ClipboardCheck, TrendingUp, Target, Dumbbell, Users];

export default function RugbyNexus() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/public/page/rugby_nexus").then((r) => setC(r.data?.content || {})); }, []);

  if (c === null) return <Loading />;
  const url = c.external_url || "https://www.rugbyhub.co.za";
  const features = c.features || [];
  const showcase = c.showcase_images || [];

  return (
    <>
      <SEO title={c.seo_title || "Rugby Nexus | Player Development at BJRC"} description={c.seo_description} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#050914]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(30,99,214,0.25),transparent_55%)]" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-10 px-4 py-20 sm:px-6 sm:py-28 lg:flex-row lg:items-center lg:px-8">
          <div className="max-w-2xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur">
              <span className="h-2 w-2 rotate-45 bg-[#E8A317]" />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">Player Development Platform</span>
            </div>
            <h1 className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">{c.heading || "Rugby Nexus"}</h1>
            <p className="mt-5 font-heading text-lg font-bold uppercase tracking-wide text-[#2E7DF6] sm:text-xl" data-testid="rugby-nexus-tagline">{c.tagline}</p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">{c.intro}</p>
            <a href={url} target="_blank" rel="noreferrer noopener" data-testid="rugby-nexus-hero-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E8A317] px-7 py-3.5 font-extrabold uppercase tracking-wide text-[#0A1626] shadow-lg transition-all hover:bg-[#CC8F12] active:scale-95">
              {c.button_text || "Discover Rugby Nexus"} <ArrowUpRight className="h-5 w-5" />
            </a>
          </div>
          {c.logo_url && (
            <div className="relative shrink-0">
              <div className="absolute -inset-8 rounded-full bg-[#1E63D6]/25 blur-3xl" />
              <img src={mediaUrl(c.logo_url)} alt="Rugby Nexus" className="relative h-44 w-44 rounded-[2rem] border border-white/15 object-cover shadow-2xl sm:h-56 sm:w-56" />
            </div>
          )}
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#1E63D6]">What Rugby Nexus Offers</span>
          <h2 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-[#0B2A4A] sm:text-4xl">A Structured Development Platform</h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div key={i} data-testid={`rugby-nexus-feature-${i}`} className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#0B2A4A] text-[#E8A317] transition-colors group-hover:bg-[#1E63D6] group-hover:text-white">
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-bold uppercase tracking-tight text-[#0B2A4A]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SHOWCASE */}
      {showcase.length > 0 && (
        <section className="bg-[#0A1128] py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-[#E8A317]">Inside the platform</p>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-slate-400">Real screens from the Rugby Nexus app, shown with demo data.</p>
            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-3">
              {showcase.slice(0, 3).map((img, i) => (
                <div key={i} className="mx-auto w-full max-w-[260px] rounded-[2rem] border border-white/15 bg-[#0A1128] p-2 shadow-2xl">
                  <div className="aspect-[640/1385] overflow-hidden rounded-[1.6rem] bg-[#050914]">
                    <img src={mediaUrl(img)} alt={`Rugby Nexus screen ${i + 1}`} loading="lazy" className="h-full w-full object-cover object-top" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* APPROACH */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#1E63D6]">Our Philosophy</span>
            <h2 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-[#0B2A4A] sm:text-4xl">{c.approach_title || "The BJRC Development Approach"}</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">{c.approach_text}</p>
            <a href={url} target="_blank" rel="noreferrer noopener" data-testid="rugby-nexus-approach-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0B2A4A] px-7 py-3.5 font-extrabold uppercase tracking-wide text-white transition-all hover:bg-[#071B30] active:scale-95">
              {c.button_text || "Discover Rugby Nexus"} <ArrowUpRight className="h-5 w-5" />
            </a>
          </div>
          {c.featured_image && (
            <div className="overflow-hidden rounded-3xl shadow-lg lg:order-last">
              <img src={mediaUrl(c.featured_image)} alt="Rugby Nexus player development" loading="lazy" className="aspect-[4/3] h-full w-full object-cover" />
            </div>
          )}
        </div>
      </section>
    </>
  );
}
